import { Link } from "react-router-dom";
import BackToDocumentary from "../../shared/BackToDocumentary/BackToDocumentary";
import { achievements, awards, experience, projects, workshops } from "../../../content";
import type {
	AchievementEntry,
	AchievementImage,
	Appreciation,
	Award,
	ExperienceEntry,
	Project,
	Workshop
} from "../../../types/content";
import { achievementImage, BROADCAST_POSTER } from "../../../utils/achievementImages";
import BroadcastVideo from "./BroadcastVideo";
import CertificateLightbox from "../../shared/CertificateLightbox/CertificateLightbox";
import { awardImages } from "../../../utils/awardImages";
import { appreciationImage, publishableAppreciations } from "../../../utils/appreciationImages";
import { longDate, monthYear } from "../../../utils/dateLabels";
import type { AwardImage } from "../../../utils/awardImages";

/**
 * ============================================================================
 * Recognition — the /achievements route (D-069).
 * ============================================================================
 *
 * Direct consumer of the content layer (no bridge, V.4). To change what this
 * page says, edit `src/content/data/achievements.json` — not this file.
 *
 * THE ATTRIBUTION IS THE POINT OF THIS PAGE, AND IT IS RENDERED FROM DATA.
 * The owner's ruling was explicit: Doordarshan featured the Aeonix team's
 * government project work, and the page must not claim a personal feature.
 * `attribution: "team"` is what produces the subject line below, so the
 * over-claim is not something a future edit to this component can reintroduce
 * — it would have to change the content file, where the ruling is recorded
 * beside it.
 *
 * AND THE PERSONAL CONNECTION IS DERIVED, NOT WRITTEN. `roleReference` is an
 * id into `experience.json` and `projectReferences` are ids into
 * `projects.json`, so the role title, its dates and the project names come out
 * of the files the resume sync owns. A sentence typed here would be a fact
 * about a career living outside `src/content` (II.2) and would drift the first
 * time the resume was synced — silently, because prose is where unsourced
 * claims survive (D-066).
 *
 * WHAT IS DELIBERATELY NOT CLAIMED. The page says which roles the layer
 * records at Aeonix during the covered period. It does NOT say those are the
 * projects the broadcast showed, because nothing in the layer settles that,
 * and the four stills show a desk rather than a caption.
 */

/**
 * The page's standfirst — used verbatim as this route's meta description
 * (D-044), so the search result cannot drift from the page's opening line.
 *
 * NO NUMBER AND NO SUPERLATIVE. It says what the page holds. The date is not
 * in it because the entries carry their own years, and "2024" written here
 * would be the second place a year lives.
 */
/* THE ORDER OF THE NOUNS FOLLOWS THE ORDER OF THE SECTIONS (D-105), AND A
   THIRD SECTION MEANS A THIRD NOUN (D-106). It read "awards conferred and
   broadcast coverage" while Awards led the page; both moved together when the
   owner asked for the broadcast first, and "letters of appreciation" joined
   them the day the Aeonix letter did. This string is also the route's
   `<meta name="description">` through `routeMeta.ts`, so it is the sentence a
   search result shows — which is the other reason it cannot be left describing
   a page that no longer exists in that shape. The sweep asserts all three
   nouns appear here in the order the sections render. */
export const ACHIEVEMENTS_STANDFIRST =
	"External recognition of the work — broadcast coverage, awards conferred and letters of appreciation, recorded as it happened and attributed to whoever earned it.";

export const QUIET_TINT = "border-[var(--cat-indigo-border)] text-[var(--cat-indigo-text)] bg-[var(--cat-indigo-fill)]";

/** Month names are never rendered — the layer holds years only for this file. */
const yearsOf = (entry: ExperienceEntry): string => {
	const start = entry.startDate.slice(0, 4);
	const end = entry.endDate === null ? "Present" : entry.endDate.slice(0, 4);
	return `${start} – ${end}`;
};

/**
 * Who the recognition was for, in words, from the enum.
 *
 * Two branches because there are two values, and neither is a default: a
 * missing attribution would have to mean one of them, and guessing which is
 * the failure the type exists to prevent.
 */
const attributionLine = (entry: AchievementEntry): string =>
	entry.attribution === "team"
		? `Attributed to ${entry.subject}, and to the team who delivered it.`
		: `Attributed to ${entry.subject}.`;

function Frames({ entry }: { entry: AchievementEntry }): JSX.Element | null {
	/* An id that names no binary renders nothing rather than a broken image —
	   and the join assertion in the verification sweep is what surfaces the
	   typo, in both directions. D-065's rule, applied to pictures. */
	const resolved = entry.images
		.map((image: AchievementImage) => ({ image, src: achievementImage(image.id) }))
		.filter(
			(r: { image: AchievementImage; src: string | undefined }): r is { image: AchievementImage; src: string } =>
				Boolean(r.src)
		);

	if (!resolved.length) return null;

	return (
		<div className="mt-10">
			<h4 className="text-base font-semibold text-[var(--text-strong)]">Frames from the segment</h4>
			{/* NOT "gallery". All four frames are one camera setup — two at
			    1600x900 and two at 1920x1080 — so presenting them as separate
			    occasions would be a claim the pictures do not support. */}
			{/* NO COUNT IN THIS SENTENCE (D-106). It read "All four are from the
			    same sequence" from D-069 until the claims gate was first pointed
			    at this page — a figure in a blurb is a second place it lives and
			    the one nothing checks (D-066), and this one would have gone
			    false the moment a fifth still was published. The cards below ARE
			    the count. */}
			<p className="mt-2 max-w-3xl text-sm text-[var(--text-muted)]">
				Stills captured from the broadcast, all from the same sequence.
			</p>
			<div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
				{resolved.map((r: { image: AchievementImage; src: string }) => (
					<figure key={r.image.id} className="card-shell m-0">
						<div className="card-face overflow-hidden p-2">
							<img
								src={r.src}
								alt={r.image.alt}
								width={1600}
								height={900}
								loading="lazy"
								decoding="async"
								className="block h-auto w-full rounded-[10px]"
							/>
						</div>
					</figure>
				))}
			</div>
			{/* --text-muted, NOT --text-faint, AND THE SWEEP IS WHY. This paragraph
			    sits on the SECTION GROUND rather than inside a card, and
			    --text-faint was tuned in D-037 against --surface-card: it is
			    card-scoped by construction, its own token comment says so, and
			    on this ground (rgb(28,28,39) in dark) it measures 4.44:1 and
			    fails the 4.5 floor. MartialArts.tsx carries the same warning
			    from the same mistake, 108 failures' worth; this file walked
			    into it anyway and the sweep caught 5. Text inside a card-face
			    below keeps --text-faint (5.03:1) and is untouched. */}
			{entry.imageNote && <p className="mt-5 max-w-3xl text-sm text-[var(--text-muted)]">{entry.imageNote}</p>}
		</div>
	);
}

function Connection({ entry }: { entry: AchievementEntry }): JSX.Element | null {
	const role = entry.roleReference
		? experience.find((e: ExperienceEntry) => e.id === entry.roleReference)
		: undefined;
	const named = (entry.projectReferences ?? [])
		.map((id: string) => projects.find((p: Project) => p.id === id))
		.filter((p: Project | undefined): p is Project => p !== undefined);

	if (!role && !named.length) return null;

	return (
		<div className="mt-10 card-shell">
			<div className="card-face p-6 sm:p-7">
				<h4 className="text-base font-semibold text-[var(--text-strong)]">The connection to this record</h4>
				{role && (
					<p className="mt-3 text-sm leading-relaxed text-[var(--text-secondary)]">
						{role.title} at {role.company}, {yearsOf(role)} — the period the feature covers.
					</p>
				)}
				{named.length > 0 && (
					<>
						<p className="mt-4 text-sm leading-relaxed text-[var(--text-secondary)]">
							The government-sector work this documentary records from that period:
						</p>
						<ul className="mt-3 space-y-2">
							{named.map((p: Project) => (
								<li key={p.id} className="text-sm text-[var(--text-muted)]">
									<span className="font-medium text-[var(--text-secondary)]">{p.name}</span> —{" "}
									{p.role}
								</li>
							))}
						</ul>
					</>
				)}
				{/* THE LIMIT OF THE CLAIM, STATED ON THE PAGE RATHER THAN LEFT
				    TO A READER'S INFERENCE. The stills show a desk, not a
				    caption; nothing in the record identifies which projects the
				    segment showed, and a page that quietly let the two lists
				    line up would be asserting it. */}
				<p className="mt-4 text-sm text-[var(--text-faint)]">
					The broadcast named the company rather than individual projects; the record above is what this
					documentary holds for the same period, not a claim about what the segment showed.
				</p>
			</div>
		</div>
	);
}

function Entry({ entry }: { entry: AchievementEntry }): JSX.Element {
	return (
		/* THE `id` IS THE LANDING SITE FOR THE HOME CARD (D-100). The card links
		   to `/achievements#<id>`, which puts a reader at the top of the entry —
		   outlet, headline, summary and attribution — rather than at the video,
		   and `BroadcastVideo` below starts the recording off the same hash. One
		   anchor, two behaviours, so they cannot disagree about which entry the
		   reader asked for. */
		<article id={entry.id} className="mt-12 scroll-mt-24 first:mt-10">
			<div className="flex flex-wrap items-center gap-3">
				<span className={`pill-tint pill-tint-upper ${QUIET_TINT}`}>{entry.outlet}</span>
				<span className={`pill-tint ${QUIET_TINT}`}>{entry.scope}</span>
				<span className="text-sm font-semibold tabular-nums text-[var(--text-muted)]">{entry.year}</span>
			</div>

			{/* h3, NOT h2 — the "Press & Broadcast" section heading above is the
			    h2 now, and a heading level that skips or repeats is a structure a
			    screen-reader user navigates by. */}
			<h3 className="mt-5 text-2xl font-bold text-[var(--text-strong)]">{entry.title}</h3>

			<p className="mt-4 max-w-3xl text-base leading-relaxed text-[var(--text-secondary)]">{entry.summary}</p>

			<p className="mt-3 max-w-3xl text-sm text-[var(--text-muted)]">{entry.programmeContext}.</p>

			<p className="mt-3 max-w-3xl text-sm text-[var(--text-muted)]">{attributionLine(entry)}</p>

			<BroadcastVideo
				video={entry.video}
				autoPlayHash={entry.id}
				poster={BROADCAST_POSTER}
				posterAlt={entry.images[0]?.alt ?? `Still from the ${entry.outlet} segment, ${entry.year}.`}
				caption={
					entry.video === null
						? "Broadcast footage — the recording will be published here."
						: `Broadcast footage, ${entry.outlet}, ${entry.year}.`
				}
			/>

			<Connection entry={entry} />
			<Frames entry={entry} />
		</article>
	);
}

/* -------------------------------------------------------------------------- */
/* Awards — rendered for the first time in D-081                              */
/* -------------------------------------------------------------------------- */

/**
 * ============================================================================
 * THIS SECTION EXISTS BECAUSE THE DATA DID AND THE PAGE DID NOT.
 * ============================================================================
 * `awards.json` has held four entries since 2026-08-07, sourced to the master
 * resume. `src/content/index.ts` exported them. **No component read the
 * export.** Verified against the BUILT HTML rather than the DOM, which is the
 * D-065 rule and the only check that answers the question a search engine
 * asks: "Mentor of the Year", "Pragati" and "Operational Excellence Award"
 * appeared in exactly one file — `stories.html` — as prose inside two career
 * narratives, and `martial-arts-medalist` appeared in no file at all.
 *
 * That is D-065's shape exactly: a publish gate answers "may this be shown?",
 * never "is this shown?", and the Certifications section rendered 9 of 21
 * verified credentials for months with every individual control working
 * correctly. Adding a record to a content JSON does not put it on a page.
 *
 * SO THERE IS NO CURATION LIST HERE, DELIBERATELY. Every award in the file
 * renders, in the file's own order, and the verification sweep asserts the join
 * in both directions against the built HTML — every entry present exactly once,
 * nothing present that the layer does not hold. The alternative was a filter
 * keeping the two martial-arts entries off this page because `/martial-arts`
 * covers them in more detail; that filter would have been a second place the
 * decision lives, which is the mechanism that hid CLSSBB.
 *
 * THE MARTIAL-ARTS DUPLICATION IS REAL AND IS NOT NEW. `awards.json` and
 * `martialArts.json` both record the 2018 Federation Cup, deliberately, and the
 * two agree because the championship's canonical name is byte-identical in both
 * and asserted as such. Rendering this file does not create that duplication —
 * it makes it visible, which is the better of the two states.
 */

/**
 * "2024-12" -> "December 2024"; a bare "2018" stays "2018".
 *
 * A NAMED RE-EXPORT OF `monthYear`, not a second implementation (D-106). It
 * kept its own twelve-string month array until the Letters section would have
 * written a third copy; the name stays because `conferrals` below and the Home
 * teaser both read as award code, and the parsing-by-hand warning now lives
 * with the parsing, in `utils/dateLabels.ts`.
 */
export const awardDate = monthYear;

/**
 * Every conferral, newest first, as one line.
 *
 * COMPUTED FROM THE DATE FIELDS, NEVER TYPED. Until D-081 the fact that Mentor
 * of the Year was won twice lived only in `description` — "for two consecutive
 * years (2023 and 2024)" — where nothing could check it against the `date`
 * beside it, which said 2024 alone. D-066's rule is that prose is where
 * unsourced numbers survive, and a year in a sentence is exactly that. The
 * dates are fields now and this renders them; the description no longer says.
 */
export const conferrals = (award: Award): string =>
	[award.date, ...(award.alsoConferredOn ?? [])]
		.slice()
		.sort((a: string, b: string) => b.localeCompare(a))
		.map(awardDate)
		.join(" · ");

function AwardCard({ award }: { award: Award }): JSX.Element {
	/* "Conferred twice" is DERIVED, so it cannot disagree with the dates
	   rendered beside it. Nothing is said at all where an award was conferred
	   once — a card reading "conferred once" is noise. */
	const times = 1 + (award.alsoConferredOn?.length ?? 0);
	const evidence: readonly AwardImage[] = awardImages(award.id);

	return (
		/* THE `id` IS THE LANDING SITE FOR THE HOME CARD (D-101), on the pattern
		   every other destination on this site took in D-099 and D-100. Every
		   award is anchored, not only the ones Home shows: an unanchored record
		   cannot be linked to later, and the gate asserts the join both ways. */
		<li id={award.id} className="card-shell m-0 scroll-mt-24">
			<div className="card-face h-full p-6">
				<div className="flex flex-wrap items-center gap-3">
					<span className={`pill-tint ${QUIET_TINT}`}>{award.context}</span>
					{times > 1 && (
						<span className={`pill-tint ${QUIET_TINT}`}>
							Conferred {times === 2 ? "twice" : `${times} times`}
						</span>
					)}
				</div>

				<h3 className="mt-4 text-lg font-bold leading-snug text-[var(--text-strong)]">{award.title}</h3>

				<p className="mt-2 text-sm text-[var(--text-muted)]">
					{/* The issuer is nullable and the two martial-arts entries have
					    none — a championship placement is conferred by the event,
					    which `context` already names. Rendering "null ·" there
					    would be worse than rendering nothing. */}
					{award.issuer !== null && (
						<>
							<span className="font-medium text-[var(--text-primary)]">{award.issuer}</span>
							<span aria-hidden="true"> · </span>
						</>
					)}
					<span className="tabular-nums">{conferrals(award)}</span>
				</p>

				<p className="mt-4 text-sm leading-relaxed text-[var(--text-secondary)]">{award.description}</p>

				{/* ------------------------------------------------------------
				    THE EVIDENCE, ADDED D-105. Until then every award on this
				    page asserted itself and offered nothing to look at — the
				    state D-092 found three ISOEH cards in, with their scans
				    sitting in `src/assets` rendering nowhere.

				    A CARD WITH NO ARTIFACT RENDERS NOTHING HERE, not an empty
				    frame and not a "certificate pending" line. Two of the four
				    awards are podium finishes, and a championship placement has
				    no certificate to withhold — so absence is the finished
				    state rather than a gap, and a visitor is not the audience
				    for an internal queue (D-069). ------------------------- */}
				{evidence.length > 0 && (
					<div className="mt-5 flex flex-wrap gap-3">
						{evidence.map((image: AwardImage) => (
							<CertificateLightbox
								key={image.src}
								src={image.src}
								alt={image.alt}
								title={`${award.title} — ${image.label}`}
								triggerLabel={`Enlarge the ${image.label.toLowerCase()} for ${award.title}`}
								triggerClassName="group/cert block w-24 shrink-0 rounded-[10px] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)] sm:w-28"
							>
								{/* `alt=""` and aria-hidden: the BUTTON already
								    names what enlarging will show, and an image
								    with alt text inside it would append to that
								    button's accessible name (D-095's rule for
								    the workshop glimpse). The full alt text is
								    on the enlarged image, where it is the only
								    description available. */}
								{/* `object-contain`, NOT `object-cover`, AND THE TROPHY IS
								    WHY. These artifacts are not all the same shape: the
								    certificates are 1280x904 landscape and the Pragati
								    trophy is 853x1280 portrait. Cover cropped the trophy
								    to its middle and cut the base plate off — the one
								    part of it that carries the owner's name, i.e. the
								    only thing that makes it evidence rather than a
								    photograph of a trophy. A thumbnail of an artifact
								    shows the artifact.

								    `h-full w-full`, NOT `max-h-full max-w-full`, AND THE
								    DIFFERENCE WAS MEASURED. With the max-* pair the
								    trophy's img painted 102x153 inside a box 76px tall —
								    it overflowed its own span and was clipped by
								    `overflow-hidden`, so `object-contain` was set and
								    doing nothing. That is D-083 exactly: a percentage
								    bound against a parent the layout has not made
								    definite is not a bound.

								    AND THE WRAPPER IS A BLOCK, NOT A GRID — the second
								    half of the same finding. With `grid place-items-center`
								    the trophy STILL measured 102x153 after the max-* pair
								    was fixed, because an implicit grid row's default
								    minimum is `auto`, i.e. the content's own height, which
								    outranks the `aspect-ratio` on the container. D-083
								    records the same thing about `minmax(0, 1fr)` versus
								    `1fr`. A plain block honours the ratio, and
								    `object-contain` centres the picture without help. */}
								<span className="block aspect-[4/3] overflow-hidden rounded-[10px] border border-[var(--border)] bg-[var(--surface)] p-1">
									<img
										src={image.src}
										alt=""
										aria-hidden="true"
										loading="lazy"
										decoding="async"
										className="h-full w-full object-contain transition-transform duration-200 group-hover/cert:scale-[1.04]"
									/>
								</span>
								<span className="mt-2 block text-center text-xs font-medium text-[var(--text-muted)]">
									{image.label}
								</span>
							</CertificateLightbox>
						))}
					</div>
				)}
			</div>
		</li>
	);
}

function Awards(): JSX.Element | null {
	/* An empty layer renders no section rather than an empty heading (II.4). */
	if (awards.length === 0) return null;

	return (
		<section className="mt-16" aria-labelledby="awards-heading">
			<h2 id="awards-heading" className="text-2xl font-bold text-[var(--text-strong)]">
				Awards
			</h2>
			{/* NO COUNT IN THIS LINE. The cards below are the count, and a number
			    written here would be the second place it lives (D-065). */}
			<p className="mt-3 max-w-3xl text-sm text-[var(--text-muted)]">
				Conferred by employers and by competition, across the engineering career and the martial-arts record.
			</p>

			<ul className="mt-8 grid list-none auto-rows-fr grid-cols-1 gap-6 p-0 lg:grid-cols-2">
				{awards.map((award: Award) => (
					<AwardCard key={award.id} award={award} />
				))}
			</ul>
		</section>
	);
}

/* -------------------------------------------------------------------------- */
/* Letters of Appreciation — added D-106                                      */
/* -------------------------------------------------------------------------- */

/**
 * ============================================================================
 * THE THIRD SECTION, AND IT EXISTS SO THAT A LETTER DID NOT BECOME AN AWARD.
 * ============================================================================
 * The Aeonix letterhead letter shipped on `/workshops/mern-training` until
 * D-087 retired its export in favour of the ornamental Certificate of
 * Achievement — on a tiebreak, with `[OWNER: replace / alongside]` returned
 * unfilled. The owner answered the open question on 2026-09-05: show the letter,
 * here.
 *
 * WHY NOT A FIFTH ROW IN `awards.json`. That file's `canonicalSource` is the
 * master resume and every row in it is an award somebody conferred. A letter of
 * thanks appended to it would be a claim the resume does not make, arriving
 * through the file the resume sync owns — and it would inherit the Awards
 * section's framing, which says "conferred". `appreciations.json` says what this
 * is instead, and the type makes its document REQUIRED, so an appreciation
 * cannot be published as prose with no scan behind it. That is the owner's
 * standing "do not invent testimonials", made unrepresentable rather than
 * remembered.
 *
 * THE CARD IS NOT A LINK, DELIBERATELY. Every other card this documentary added
 * in D-099 through D-101 stretches an anchor over itself with `after:inset-0`.
 * This one holds a lightbox trigger AND a link to the workshop, so stretching
 * either would put two targets on one surface and make which one a click lands
 * on a question of stacking order. Two explicit controls, both reachable by
 * keyboard, is the honest arrangement for a card with two destinations.
 */
function LetterCard({ appreciation }: { appreciation: Appreciation }): JSX.Element | null {
	/* BOTH KEYS AGAIN, though `publishableAppreciations` already applied them.
	   A component that renders whatever it is handed is one prop away from
	   rendering an uncleared scan, and the cost of asking twice is one line. */
	const src: string | undefined =
		appreciation.image.status === "published" ? appreciationImage(appreciation.image.id) : undefined;
	/* Guaranteed by the loader, which throws at module load on an unresolved
	   reference — but the narrowing is what lets the fields below be read. */
	const work: Workshop | undefined = workshops.find((entry: Workshop) => entry.id === appreciation.workshopReference);
	if (src === undefined || work === undefined) return null;

	return (
		/* Anchored like every other record on this page (D-100/D-101), so it can
		   be linked to later. Every entry is anchored, not only the ones some
		   other surface happens to point at today. */
		<li id={appreciation.id} className="card-shell m-0 scroll-mt-24">
			<div className="card-face h-full p-6 sm:p-7">
				<div className="flex flex-col gap-6 sm:flex-row sm:items-start">
					<div className="min-w-0 flex-1">
						{/* The role comes from the WORKSHOP record, not from this
						    letter: what the owner did is a fact the layer already
						    owns, and `roleStated` below is a different claim —
						    how the ISSUER described him, which is theirs. */}
						<span className={`pill-tint ${QUIET_TINT}`}>{work.role}</span>

						<h3 className="mt-4 text-lg font-bold leading-snug text-[var(--text-strong)]">
							{appreciation.title}
						</h3>

						<p className="mt-2 text-sm text-[var(--text-muted)]">
							<span className="font-medium text-[var(--text-primary)]">{appreciation.issuer}</span>
							<span aria-hidden="true"> · </span>
							<span className="tabular-nums">{longDate(appreciation.date)}</span>
						</p>

						<p className="mt-4 text-sm leading-relaxed text-[var(--text-secondary)]">
							{appreciation.summary}
						</p>

						{/* VERBATIM, IN A BLOCKQUOTE, AND NOWHERE ELSE ON THIS CARD
						    IS THE ISSUER'S VOICE USED. A paraphrase of praise reads
						    as the site's own opinion of itself; the quotation marks
						    are the whole point of the element. */}
						<blockquote className="mt-5 border-l-2 border-[var(--border)] pl-4 text-sm italic leading-relaxed text-[var(--text-secondary)]">
							&ldquo;{appreciation.quote}&rdquo;
						</blockquote>

						<p className="mt-4 text-sm text-[var(--text-muted)]">
							The letter names the role as{" "}
							<span className="font-medium text-[var(--text-secondary)]">
								&ldquo;{appreciation.roleStated}&rdquo;
							</span>
							.
						</p>

						{/* THE WORK LIVES ON ITS OWN PAGE AND IS LINKED RATHER THAN
						    RESTATED (D-094). Title, host and date are the workshop
						    record's, so a corrected date reaches this card without
						    anyone remembering it exists. */}
						<p className="mt-4 text-sm text-[var(--text-muted)]">
							The work it records:{" "}
							<Link
								to={`/workshops/${work.slug}`}
								className="inline-block min-h-[24px] font-medium text-[var(--accent-link)] transition-colors duration-200 hover:text-[var(--accent-link-hover)]"
							>
								{work.title}
							</Link>
							<span aria-hidden="true"> — </span>
							{work.host}, <span className="tabular-nums">{monthYear(work.date)}</span>
						</p>
					</div>

					{/* PORTRAIT, WHERE THE AWARD THUMBNAILS ARE 4:3. This document is
					    1079x1525 and a landscape frame would letterbox it to a
					    sliver. `object-contain` inside a definite box, block wrapper
					    — the pair D-105 had to measure twice to get right. */}
					<CertificateLightbox
						src={src}
						alt={appreciation.image.alt}
						title={`${appreciation.title} — ${appreciation.issuer}`}
						triggerLabel={`Enlarge the letter of appreciation from ${appreciation.issuer}`}
						triggerClassName="group/letter block w-28 shrink-0 self-start rounded-[10px] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)] sm:w-32"
					>
						<span className="block aspect-[3/4] overflow-hidden rounded-[10px] border border-[var(--border)] bg-[var(--surface)] p-1">
							<img
								src={src}
								alt=""
								aria-hidden="true"
								loading="lazy"
								decoding="async"
								className="h-full w-full object-contain transition-transform duration-200 group-hover/letter:scale-[1.04]"
							/>
						</span>
						<span className="mt-2 block text-center text-xs font-medium text-[var(--text-muted)]">
							The letter
						</span>
					</CertificateLightbox>
				</div>
			</div>
		</li>
	);
}

function Letters(): JSX.Element | null {
	/* An empty layer renders no section rather than an empty heading (II.4) —
	   and "empty" here means the two-key gate, not the file: an entry whose
	   scan is not cleared yet leaves this section absent rather than shipping a
	   card that quotes a document nobody can see. */
	if (publishableAppreciations.length === 0) return null;

	return (
		<section className="mt-16" aria-labelledby="letters-heading">
			<h2 id="letters-heading" className="text-2xl font-bold text-[var(--text-strong)]">
				Letters of Appreciation
			</h2>
			{/* NO COUNT IN THIS LINE, on the Awards section's reasoning (D-065). */}
			<p className="mt-3 max-w-3xl text-sm text-[var(--text-muted)]">
				Written by the organisations the work was delivered for. Each one is quoted in its own words, with the
				document beside it.
			</p>

			<ul className="mt-8 grid list-none grid-cols-1 gap-6 p-0">
				{publishableAppreciations.map((appreciation: Appreciation) => (
					<LetterCard key={appreciation.id} appreciation={appreciation} />
				))}
			</ul>
		</section>
	);
}

export default function Achievements(): JSX.Element {
	return (
		<section className="section-shell" aria-labelledby="achievements-heading">
			<div className="section-rail">
				<p className="section-eyebrow">External Recognition</p>

				{/* THE ROUTE'S ONLY <h1>. Every route ships exactly one (D-042). */}
				<h1 id="achievements-heading" className="heading">
					<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
						Recognition
					</span>
				</h1>

				<p className="section-standfirst">{ACHIEVEMENTS_STANDFIRST}</p>

				{/* ------------------------------------------------------------------
				    PRESS & BROADCAST LEADS THIS PAGE — owner instruction, 2026-09-03
				    (D-105). It sat below Awards from D-081 until then.

				    THE STANDFIRST MOVED WITH IT, and that is the point of recording
				    the swap rather than just making it. The line read "awards
				    conferred and broadcast coverage" — a reader meets the standfirst
				    first, and a page whose opening sentence lists its sections in the
				    opposite order to the sections themselves is a small, permanent
				    contradiction of exactly the kind D-066 is about. Two orderings of
				    one page is one too many.

				    IT KEEPS ITS OWN HEADING, unchanged: two different claims about two
				    different subjects — an award conferred on a person, and a
				    broadcaster filming a company's work — and D-067 is the record of
				    what happens when two such claims share one heading. Reordering
				    them does not merge them.
				    ------------------------------------------------------------------ */}
				<section className="mt-16" aria-labelledby="press-heading">
					<h2 id="press-heading" className="text-2xl font-bold text-[var(--text-strong)]">
						Press &amp; Broadcast
					</h2>
					{achievements.map((entry: AchievementEntry) => (
						<Entry key={entry.id} entry={entry} />
					))}
				</section>

				<Awards />

				<Letters />

				{/* THE ONLY CONTENT ROUTE THAT HAD NO WAY BACK (D-107). Seven others
				    carry this link and the responsive audit found it here by its
				    absence rather than by a measurement — which is the shape D-065
				    keeps taking: what is missing is invisible, and only a check that
				    asks the question in both directions sees it. */}
				<div className="mt-16 text-center">
					<BackToDocumentary />
				</div>
			</div>
		</section>
	);
}
